import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { agentService } from '../../services/agentService';
import { useNotificationStore } from '../../store/notificationStore';
import { CodeEditor } from '../common/CodeEditor';
import type { AgentMarkdownFileDto } from '../../types/agent';

interface AgentMarkdownFileListProps {
  agentId: string;
  readOnly?: boolean;
}

/**
 * List and CRUD for agent markdown files (File Search knowledge files).
 */
export const AgentMarkdownFileList = ({ agentId, readOnly = false }: AgentMarkdownFileListProps) => {
  const [files, setFiles] = useState<AgentMarkdownFileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFile, setEditingFile] = useState<AgentMarkdownFileDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formSortOrder, setFormSortOrder] = useState(0);
  const { addNotification } = useNotificationStore();

  const loadFiles = async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getMarkdownFiles(agentId);
      setFiles(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load markdown files';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [agentId]);

  const handleAdd = () => {
    setEditingFile(null);
    setFormName('');
    setFormContent('');
    setFormSortOrder(files.length);
    setOpenDialog(true);
  };

  const handleEdit = (file: AgentMarkdownFileDto) => {
    setEditingFile(file);
    setFormName(file.name);
    setFormContent(file.content);
    setFormSortOrder(file.sortOrder);
    setOpenDialog(true);
  };

  const handleDelete = async (file: AgentMarkdownFileDto) => {
    if (!window.confirm(`Delete "${file.name}"?`)) return;
    try {
      await agentService.deleteMarkdownFile(agentId, file.id);
      addNotification('File deleted', 'success');
      loadFiles();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete file', 'error');
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingFile(null);
  };

  const handleDialogSave = async () => {
    const name = formName.trim();
    if (!name) {
      addNotification('Name is required', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingFile) {
        await agentService.updateMarkdownFile(agentId, editingFile.id, {
          name,
          content: formContent,
          sortOrder: formSortOrder,
        });
        addNotification('File updated', 'success');
      } else {
        await agentService.createMarkdownFile(agentId, {
          name,
          content: formContent,
          sortOrder: formSortOrder,
        });
        addNotification('File added', 'success');
      }
      handleDialogClose();
      loadFiles();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to save file', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Markdown files (File Search)</Typography>
        {!readOnly && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
            Add file
          </Button>
        )}
      </Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {files.length === 0 ? (
        <Typography color="text.secondary" paragraph>
          No markdown files yet. Add files to enable the agent to search this knowledge during conversations (File Search tool).
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Order</TableCell>
                {!readOnly && <TableCell align="right" width={120}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>{file.name}</TableCell>
                  <TableCell align="right">{file.sortOrder}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEdit(file)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(file)} color="error" aria-label="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingFile ? 'Edit markdown file' : 'Add markdown file'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="File name"
            fullWidth
            variant="outlined"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Sort order"
            type="number"
            fullWidth
            variant="outlined"
            value={formSortOrder}
            onChange={(e) => setFormSortOrder(parseInt(e.target.value, 10) || 0)}
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Content (Markdown)
          </Typography>
          <CodeEditor
            value={formContent}
            onChange={setFormContent}
            language="markdown"
            height="320px"
            readOnly={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : editingFile ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
